import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Flex, Select, Spinner, Text, useDisclosure, SimpleGrid, Alert, AlertIcon } from '@chakra-ui/react';
import PageControl from './PageControl';
import CustomMemberTable from './CustomMemberTable'; // Import CustomMemberTable
import Pagination from './Pagination';
import { supabase } from "../../../../api/supabase";
import { useHistory } from "react-router-dom";
import TableOperations from './utils/TableOperations';

const BaseMemberActivityPageComponent = ({
  txt,
  fetchData = () => Promise.resolve(),
  addItemComponent: AddEditModal = null,
  itemCardComponent: ItemCard = null,
  itemCardListComponent: ItemCardList = null,
  searchPlaceholder = '',
  filterFields = [],
  sortFields = [],
  defaultSortKey = 'id',
  defaultSortOrder = 'asc',
  showAddIcon = true, // Updated prop: can be boolean or 'always', 'never', 'hideWhenData'
  showFilter = true,
  showSearch = false,
  showSort = true,
  showPagination = false,
  showActions = true,
  hideDelete = false,
  hideEdit = false,
  hideView = false,
  itemsPerPageOptions = [5, 10, 20, 50],
  tableName = '',
  joins = [],
  primaryKey = 'id',
  tableHeaders = [], // Custom keys for table headers
  keyMappings = {}, // Key mappings for display names
  saveData = () => Promise.resolve(), // Function to save data (create/update)
  gridView = false,
  tempData = [],
  columns = [],
  searchFields = null,
  dateCondition = {
    type: 'none', // 'none', 'single', 'range', or 'custom'
    fields: {
      start: 'startDate',
      end: 'endDate',
      single: 'effectiveDate'
    },
    customLogic: null
  },
  disabledButtons = [], // Array of buttons to disable: ['edit', 'delete']
  sortableHeader = null,
  isMultiLanguageSupported = false,

}) => {
  const history = useHistory();
  const [data, setData] = useState(tempData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState(`${defaultSortKey}-${defaultSortOrder}`);
  const [totalPages, setTotalPages] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0]);
  const [isGridView, setIsGridView] = useState(gridView);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [alert, setAlert] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEdit, setIsEdit] = useState(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });

  const handleSort = useCallback((field) => {
    if (field === sortableHeader) {
      setSortConfig((prevConfig) => ({
        field,
        direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
      }));
    }
  }, [sortableHeader]);

  const sortedData = React.useMemo(() => {
    if (sortConfig.field) {
      return [...data].sort((a, b) => {
        if (a[sortConfig.field] < b[sortConfig.field]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.field] > b[sortConfig.field]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [data, sortConfig]);
  // Updated function to determine whether to show the add icon
  const shouldShowAddIcon = useCallback(() => {
    if (typeof showAddIcon === 'boolean') {
      return showAddIcon;
    }
    switch (showAddIcon) {
      case 'never':
        return false;
      case 'hideWhenData':
        return data.length === 0;
      case 'always':
      default:
        return true;
    }
  }, [showAddIcon, data.length]);

  // Checks if the current date is within the effective date range for a given item.
  const isButtonDisabled = useCallback((item, buttonType) => {
    if (!disabledButtons.includes(buttonType)) {
      return false;
    }

    const currentDate = new Date();

    switch (dateCondition.type) {
      case 'single':
        const effectiveDate = new Date(item[dateCondition.fields.single]);
        return currentDate < effectiveDate;

      case 'range':
        const startDate = new Date(item[dateCondition.fields.start]);
        const endDate = new Date(item[dateCondition.fields.end]);
        return currentDate >= startDate && currentDate <= endDate;

      case 'custom':
        return dateCondition.customLogic ? dateCondition.customLogic(item, currentDate) : false;

      case 'none':
      default:
        return false;
    }
  }, [dateCondition, disabledButtons]);

  // Clear alert after 2 seconds
  useEffect(() => {
    if (alert && alert.status === "success") {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
    handleRefresh();
  }, [alert, currentPage, itemsPerPage]);

  // 确保 filteredData 是数组
  const filteredData = Array.isArray(data) ? data : [];

  // 使用 filteredData 进行分页
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleRefresh = useCallback(async () => {
    setLoading(true);
  
    try {
      let result;
      if (tableName === 'member_points_history' && tempData?.id) {
        result = await getMemberPointsHistory(tempData.id);
      } else {
        result = await fetchTableData();
        if (tableName === "activity_prize") {
          result = result.data;
        }
      }
      result = await processTableData(result);
      const paginatedResult = paginateData(result);
      await setProcessedData(paginatedResult);

    } catch (error) {

      setError(error);
    } finally {
      setLoading(false);
    }
  }, [joins, filter, filterFields, tableName, currentPage, itemsPerPage]);

  // 获取表格数据
  const fetchTableData = async () => {
    if (isMultiLanguageSupported) {
      if (tableName === "activity") {
        return await TableOperations[tableName].query();
      }
      if (tableName === "activity_prize") {
        const activityId = localStorage.getItem('game_record_item_Id')
        const dataToSend = {

          post_id: activityId, // 将 primaryKey 的值命名为 id
        };
        return await TableOperations[tableName].query(dataToSend);
      }
      const tableNewName = tableName.replace(/_translation$/, "");
      const response = await TableOperations[tableName].query();
      return response[tableNewName];
    }
    if (tableName === "users_profile") {
      return await TableOperations[tableName].query();
    }
    if (tableName === "game_record") {
      const activityId = localStorage.getItem('itemId');
      const dataToSend = {
        post_id: activityId,
        keyword: searchQuery || ''
      };
      return await TableOperations[tableName].query(dataToSend);
    }
    return await global.genericapi.fetchRecords(tableName, joins);
  };

  // 处理表格数据
  const processTableData = async (result) => {
    // 应用过滤器
    if (filter && filterFields.length > 0) {
      result = result.filter(item => item[filterFields[0].value] === filter);
    }

    // 排序逻辑
    if (tableName === "users_profile") {
      return result.sort((a, b) => {
        if (b.is_active !== a.is_active) {
          return b.is_active ? 1 : -1;
        }
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
    }

    return result.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  };

  // 分页处理
  const paginateData = (result) => {
    const totalRecords = result.length;
    setTotalPages(Math.ceil(totalRecords / itemsPerPage));

    const offset = (currentPage - 1) * itemsPerPage;
    return result.slice(offset, offset + itemsPerPage);
  };

  // 设置处理后的数据
  const setProcessedData = async (paginatedData) => {
    console.log(paginatedData, "paginatedData")
    if (!isMultiLanguageSupported) {
      setData(paginatedData);
      return;
    }

    const defaultLanguage = "en";
    const processedData = paginatedData.map(item => {
      const defaultTranslation = item.translations?.find(t => t.language === defaultLanguage) || {};
      const parentId = item.id;

      if (tableName === 'activity_prize') {
        return {
          ...item,
          parentId,
          ...defaultTranslation
        };

      } 
      if (tableName === 'game') {
        return {
          ...item,
          parentId,
          ...defaultTranslation
        };

      }
      if (tableName === 'events_translation') {
        return {
          ...item,
          parentId,
          ...defaultTranslation,
          registration_period: formatDatePeriod(
            defaultTranslation.registration_start,
            defaultTranslation.registration_end
          ),
          publish_period: formatDatePeriod(
            defaultTranslation.publish_start,
            defaultTranslation.publish_end
          )
        };


      }
      if (tableName === "activity") {
        const formattedPrizes = defaultTranslation.prizes?.map(prize => ({
          name: prize.name,
          amount: prize.amount,
          prize_id: prize.prize_id,
          probability: prize.probability
        })) || [];

        const sortedPrizes = formattedPrizes.sort((a, b) => a.prize_id - b.prize_id);
        const playTypeOptions = [
          { value: 'draw_code', label: 'Draw Code' },
          { value: 'email', label: 'Email' },
          { value: 'phone', label: 'Phone' }
        ];
        const statusTypeOptions = [
          { value: 'draft', label: 'Draft' },
        { value: 'active', label: 'Active' },
        { value: 'in_active', label: 'Inactive' },
        { value: 'completed', label: 'Completed' },
        ];
        const playTypeMap = playTypeOptions.reduce((acc, cur) => {
          acc[cur.value] = cur.label; // 将 value 作为键，label 作为值
          return acc;
        }, {});
        const statusTypeMap = statusTypeOptions.reduce((acc, cur) => {
          acc[cur.value] = cur.label; // 将 value 作为键，label 作为值
          return acc;
        }, {});
        const playTypeLabel = playTypeMap[defaultTranslation.play_type] || 'Unknown Type'
        const statusTypeLabel = statusTypeMap[defaultTranslation.status] || 'Unknown Type'
        const prizeString = sortedPrizes.map(prize =>
          `${prize.name}, ${prize.amount}, ${prize.probability}`
        ).join('\n');
        return {
          ...item,
          activityId: item.id,
          ...defaultTranslation,
          publish_period: formatDateUtcPeriod(
            defaultTranslation.publish_start,
            defaultTranslation.publish_end
          ),
          event_period: formatDateUtcPeriod(
            defaultTranslation.event_start,
            defaultTranslation.event_end
          ),
          play_type: playTypeLabel,
          status: statusTypeLabel,
          prize: prizeString,
        };
      }
      return {
        ...item,
        ...defaultTranslation
      };

    });

    console.log(processedData, "processe333dData")
    setData(processedData);
  };

  // 辅助函数：格式化日期区间
  const formatDatePeriod = (startDate, endDate) => {
    return `${formatDate(startDate) || ''} - ${formatDate(endDate) || ''}`;
  };
  const formatDateUtcPeriod = (startDate, endDate) => {
    return `${formatDateToNormal(startDate) || ''} - ${formatDateToNormal(endDate) || ''}`;
  };

  const handleSearch = useCallback(async (e) => {
    const keyword = e ? e.target.value : searchQuery; // 如果是直接调用，使用当前的 searchQuery
    setSearchQuery(e.target.value);
    setCurrentPage(1);
    try {
      
      if (tableName === "game_record") {
        const activityId = localStorage.getItem('itemId'); 
        const dataToSend = {
          post_id: activityId,
          keyword: keyword
        };
        const data = await TableOperations[tableName].query(dataToSend);
        const totalPages = Math.ceil(data.length / itemsPerPage);
        setTotalPages(totalPages);
        const processedData = await processTableData(data);
        const paginatedResult = paginateData(processedData);
        await setProcessedData(paginatedResult);
        return;
   
      }
  
      // 通用表处理逻辑
      const searchCondition = `${searchFields}.ilike.%${keyword}%`;
      
      // 获取总数
      const { count } = await supabase
        .from(tableName)
        .select('*', { count: 'exact' })
        .or(searchCondition);
      
      setTotalPages(Math.ceil(count / itemsPerPage));
      const offset = (currentPage - 1) * itemsPerPage; 
      const { data: result } = await supabase
        .from(tableName)
        .select('*')
        .or(searchCondition)
        .range(offset, offset + itemsPerPage - 1);
  
      setData(result || []);
  
    } catch (error) {
      console.error("搜索请求失败:", error);
      setError(error);
      setData([]);
    }
  }, [tableName, itemsPerPage, searchFields]);


  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setItemsPerPage(itemsPerPageOptions[0]);
    handleRefresh();
  }, []);


  const handleSortChange = useCallback((sortField) => {
    setSort((prevSort) => {
      const [field, direction] = prevSort.split('-');
      const newDirection = field === sortField && direction === 'asc' ? 'desc' : 'asc';
      return `${sortField}-${newDirection}`;
    });
    handleRefresh();
  }, []);

  const handlePageChange = useCallback(async (page) => {
    setCurrentPage(page);
    setSearchQuery(searchQuery);
    // 重新请求数据时使用当前的 keyword
    if (tableName === "game_record") {
        const activityId = localStorage.getItem('itemId'); 
        const dataToSend = {
            post_id: activityId,
            keyword: searchQuery // 使用当前的搜索查询
        };
        const data = await TableOperations[tableName].query(dataToSend);
        const totalPages = Math.ceil(data.length / itemsPerPage);
        setTotalPages(totalPages);
        const processedData = await processTableData(data);
        const paginatedResult = paginateData(processedData);
        await setProcessedData(paginatedResult);
    } else if (tableName === "member_points_history") {

    } else {
        // 处理其他表的分页逻辑
        await handleSearch({ target: { value: searchQuery } }); // 重新调用 handleSearch
    }
}, [searchQuery, tableName]);

  const handleItemsPerPageChange = (e) => {
    if (!e || !e.target) {
      console.error("Event or target is undefined");
      return;
    }

    const items = e.target.value;
    if (!isNaN(items)) {
      setItemsPerPage(Number(items));
      setCurrentPage(1);
    }
  };

  const handleToggleView = useCallback(() => {
    setIsGridView(!isGridView);
  }, [isGridView]);

  const sortData = (data, sort) => {
    const [field, direction] = sort.split('-');
    return data.sort((a, b) => {
      if (direction === 'asc') {
        return a[field] > b[field] ? 1 : -1;
      }
      return a[field] < b[field] ? 1 : -1;
    });
  };

  const handleDelete = async (item) => {
    setLoading(true);
    try {
      if (isMultiLanguageSupported === false) {
        if (tableName === "users_profile") {
          const payload = { p_id: item[primaryKey] };
          console.log(item[primaryKey], "item[primaryKey]")
          await TableOperations[tableName].delete(payload);
        } else {
          await global.genericapi.deleteRecord(tableName, item[primaryKey], primaryKey);
        }
      } else {
        let parentObject;
        if (tableName === "game") {
          parentObject = { p_event_id: item.id };
        }
        else {
          parentObject = { p_event_id: item.parentId };
        }
        await TableOperations[tableName].delete(parentObject);
      }
      handleRefresh();
    } catch (error) {
      setAlert({ title: "Error deleting item", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {

    if (tableName === "activity") {
      history.push({
        pathname: "/admin/activity/detail",
        state: { item },
      });
      return
    }
    console.log("Before editing:", item);
    if (isMultiLanguageSupported === true) {
      const translations = item.translations?.reduce((acc, translation) => {
        acc[translation.language] = translation;
        return acc;
      }, {}) || {};
      setSelectedItem({
        ...item,
        translations,
      });

    } else {
      setSelectedItem(item);
    }

    setIsEdit(true);
    onOpen();
  };

  const handleAdd = () => {
    setSelectedItem(" ");
    setIsEdit(false);
    onOpen();
    console.log("handleAdd triggered");
  };

  const handleView = (item, tableName) => {
    console.log(item)
    localStorage.setItem('game_record_item_Id', item.activityId);
    history.push({
      pathname: "/admin/activity/dashboard",
      state: { item },
    });
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
    setIsPreviewOpen(false);
  };

  const handleFav = async (item) => {
    console.log('handleFav called with item:', item);
    setLoading(true);
    try {
      await global.genericapi.updateRecord(tableName, item[primaryKey], { fav: !item.fav }, primaryKey);
      handleRefresh();
    } catch (error) {
      setAlert({ title: "Error updating favorite", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEnableDisable = async (item) => {
    console.log('handleEnableDisable called with item:', item);
    setLoading(true);
    try {
      await global.genericapi.updateRecord(tableName, item[primaryKey], { enabled: !item.enabled }, primaryKey);
      handleRefresh();
    } catch (error) {
      setAlert({ title: "Error updating status", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (item) => {
    console.log('handleToggleActive called with item:', item);
    setLoading(true);
    try {
      await global.genericapi.updateRecord(tableName, item[primaryKey], { is_active: !item.is_active }, primaryKey);
      handleRefresh();
    } catch (error) {
      setAlert({ title: "Error updating activation status", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    handleRefresh();
  };

  const formatDate = (isoDateString) => {
    if (!isoDateString) return '';
    const date = new Date(isoDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };
  const formatDateToNormal = (isoDateString) => {
    if (!isoDateString) return '';
    const date = new Date(isoDateString);
    const localDateStr = date.toLocaleString('sv-SE', { timeZone: 'Asia/Hong_Kong' });
    const [datePart, timePart] = localDateStr.split(' ');
    const [year, month, day] = datePart.split('-');
    const [hours, minutes] = timePart.split(':');

    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };
  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button key={i} onClick={() => setCurrentPage(i)} isActive={currentPage === i} mx={1} size="sm">
          {i}
        </Button>
      );
    }
    return pages;
  };
  const getMemberPointsHistory = async (memberId) => {
    try {
      const { data, error } = await supabase
        .from('member_points_history')
        .select('*')
        .eq('member_id', memberId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('获取积分历史记录错误:', error);
        throw error;
      }
      const reuslt = data.map(item => {
        if (item.points !== undefined && item.is_addition !== undefined) {
          const points = item.points;
          return {
            ...item,
            points: item.is_addition ? `+${points}` : `-${points}`
          };
        }
        return item;
      });
      console.log('获取到的积分历史:', reuslt);
      return reuslt || [];
    } catch (error) {
      console.error('查询积分历史记录失败:', error.message);
      throw error;
    }
  };

  return (
    <Box p={1}>
      {alert && (
        <Alert status={alert.status} mb={1}>
          <AlertIcon />
          {alert.title}
        </Alert>
      )}
      <PageControl
        txt={txt}
        onAddClick={handleAdd}
        onSearchChange={searchPlaceholder?.length === 0 ? null : handleSearch}
        searchPlaceholder={searchPlaceholder?.length === 0 ? null : searchPlaceholder}
        searchQuery={searchPlaceholder?.length === 0 ? null : searchQuery}
        onSearchClear={searchPlaceholder?.length === 0 ? null : handleSearchClear}
        filterFields={filterFields}
        filter={filter}
        sortFields={sortFields}
        onSortChange={sortFields?.length === 0 ? null : handleSortChange}
        sort={sort}
        showAddIcon={shouldShowAddIcon()} // Use the updated function here
        showSearch={showSearch}
        showFilter={showFilter}
        showSort={showSort}
        isGridView={isGridView}
        onToggleView={handleToggleView}
        onRefresh={handleRefresh}
      />

      {loading ? (
        <Box w="100%" display="flex" justifyContent="center" alignItems="center" p={1}>
          <Spinner size="xl" />
        </Box>
      ) : error ? (
        <Text color="red.500">{error.message}</Text>
      ) : data.length === 0 ? (
        <Text mt={6}>No data available</Text> // Empty data style
      ) : isGridView ? (
        <SimpleGrid columns={[1, null, 3]} spacing={6} mt={6}>
          {paginatedData.map((item) => (
            <ItemCard
              key={item[primaryKey]}
              item={item}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onFav={handleFav}
              onEnableDisable={handleEnableDisable}
              onView={handleView}
              onToggleActive={handleToggleActive}
            />
          ))}
        </SimpleGrid>
      ) : (
        <CustomMemberTable
          data={sortableHeader ? sortedData : data || []}
          sortableHeader={sortableHeader}
          tableHeaders={tableHeaders}
          sort={sort}
          handleSortChange={handleSortChange}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleSort={handleSort}
          sortConfig={sortConfig}
          handleFav={handleFav}
          handleEnableDisable={handleEnableDisable}
          handleToggleActive={handleToggleActive}
          keyMappings={keyMappings} // Pass key mappings to CustomMemberTable
          columns={columns}
          isViewDisabled={(item) => isButtonDisabled(item, 'view')}
          isEditDisabled={(item) => isButtonDisabled(item, 'edit')}
          isDeleteDisabled={(item) => isButtonDisabled(item, 'delete')}
          currentPage={currentPage} // Add this line
          itemsPerPage={itemsPerPage}
          showActions={showActions}
          hideView={hideView}
          hideEdit={hideEdit}
          hideDelete={hideDelete}
          tableName={tableName}
        />
      )}

      {showPagination && (
        <Flex mt={4} justify="space-between" align="center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            itemsPerPageOptions={itemsPerPageOptions}
            setCurrentPage={setCurrentPage}
          />
        </Flex>
      )}

      {AddEditModal && (
        <AddEditModal
          isOpen={isOpen}
          onClose={onClose}
          onRefresh={handleRefresh}
          initialData={selectedItem}
          isEdit={isEdit}
          onSave={handleSave}
        />
      )}


    </Box>
  );
};

export default BaseMemberActivityPageComponent;
